// src/context/AuthContext.jsx


import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  updateProfile,
  getAuth,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  onSnapshot,
  collection,
  getDocs,
} from "firebase/firestore";
import app, { auth, db } from "../firebase/config";
import { initializeApp, deleteApp } from "firebase/app";

// ── Context ────────────────────────────────────
const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

// ── Provider ───────────────────────────────────
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser]   = useState(null);
  const [userProfile, setUserProfile]   = useState(null);
  const [loading, setLoading]           = useState(true);
  const [deactivatedError, setDeactivatedError] = useState("");

  const profileUnsubRef = useRef(null);

  useEffect(() => {
    // Listen for auth state changes and load user profile
    setLoading(true);
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        const unsubProfile = onSnapshot(
          doc(db, "users", user.uid),
          async (docSnap) => {
            if (!docSnap.exists()) {
              console.warn(`User document does not exist for UID: ${user.uid}. Force logging out.`);
              setDeactivatedError("Your account has been deactivated. Please contact the administrator.");
              await signOut(auth);
              setLoading(false);
              return;
            }
            const userData = docSnap.data();
            if (
              userData.isActive === false ||
              userData.status === "Inactive" ||
              userData.status === "Deactivated" ||
              userData.status === "Suspended"
            ) {
              console.warn(`User status is deactivated for UID: ${user.uid}. Force logging out.`);
              setDeactivatedError("Your account has been deactivated. Please contact the administrator.");
              await signOut(auth);
              setLoading(false);
              return;
            }
            setUserProfile(userData);
            setLoading(false);
          },
          (error) => {
            console.error("AuthContext onSnapshot error:", error);
            setLoading(false);
          }
        );
        profileUnsubRef.current = unsubProfile;
      } else {
        setCurrentUser(null);
        setUserProfile(null);
        if (profileUnsubRef.current) {
          profileUnsubRef.current();
          profileUnsubRef.current = null;
        }
        setLoading(false);
      }
    });
    return () => {
      unsubscribe();
      if (profileUnsubRef.current) {
        profileUnsubRef.current();
        profileUnsubRef.current = null;
      }
    };
  }, []); // runs on mount

  // ── login ──────────────────────────────────────────────────
  async function login(email, password) {
    setDeactivatedError("");
    // Sign in with Firebase Auth first (need UID to check Firestore)
    const credential = await signInWithEmailAndPassword(auth, email, password);

    // Check account status in Firestore after sign-in
    const userDocRef = doc(db, "users", credential.user.uid);
    const userDocSnap = await getDoc(userDocRef);

    let isDeactivatedUser = false;

    if (userDocSnap.exists()) {
      const userData = userDocSnap.data();
      if (
        userData.isActive === false ||
        userData.status === "Inactive" ||
        userData.status === "Deactivated" ||
        userData.status === "Suspended"
      ) {
        isDeactivatedUser = true;
      }
    } else {
      // If the document does not exist in `users` collection, check if present in `archived_users`
      const archivedDocRef = doc(db, "archived_users", credential.user.uid);
      const archivedDocSnap = await getDoc(archivedDocRef);
      if (archivedDocSnap.exists()) {
        isDeactivatedUser = true;
      } else {
        // Document exists in Firebase Auth but not in active/archived collections
        isDeactivatedUser = true;
      }
    }

    if (isDeactivatedUser) {
      console.warn(`Deactivated user attempted to log in: ${email}`);
      // Set the error in context FIRST so LoginPage reads it before re-render
      setDeactivatedError("Your account has been deactivated. Please contact the administrator.");
      // Then sign out to prevent session creation
      await signOut(auth);
      // Also throw so LoginPage catch block can handle it
      const err = new Error("Your account has been deactivated. Please contact the administrator.");
      err.code = "auth/user-deactivated";
      throw err;
    }

    return credential.user;
  }

  // ── logout ─────────────────────────────────────────────────
  async function logout() {
    await signOut(auth);
    // onAuthStateChanged fires → clears currentUser + userProfile
  }

  // ── resetPassword ──────────────────────────────────────────
  async function resetPassword(email) {
    await sendPasswordResetEmail(auth, email);
  }

// Valid employment types
const VALID_EMPLOYMENT_TYPES = ["full-time", "paid-intern", "unpaid-intern"];

// Helper: normalize legacy "intern" value to "paid-intern" for safe migration
function normalizeEmploymentType(type) {
  if (type === "intern") return "paid-intern";
  if (VALID_EMPLOYMENT_TYPES.includes(type)) return type;
  return "full-time";
}

// Helper: returns true if the employment type is any intern variant
function isInternType(type) {
  return type === "paid-intern" || type === "unpaid-intern" || type === "intern";
}

// Helper function to generate a guaranteed unique next Employee ID by checking active and archived collections
async function generateUniqueEmployeeId(role, employmentType) {
  let prefix = "EMP-";
  let padLen = 4;
  
  if (isInternType(employmentType)) {
    prefix = "INTERN-";
    padLen = 3;
  } else if (role === "admin") {
    prefix = "ADMIN-";
    padLen = 3;
  } else if (role === "hr") {
    prefix = "HR-";
    padLen = 4;
  } else if (role === "lead") {
    prefix = "TL-";
    padLen = 4;
  } else {
    prefix = "EMP-";
    padLen = 4;
  }

  // Fetch both active and archived users to never reuse IDs
  const usersSnapshot = await getDocs(collection(db, "users"));
  const archivedSnapshot = await getDocs(collection(db, "archived_users"));

  const allIds = new Set();
  usersSnapshot.forEach(docSnap => {
    const id = docSnap.data().employeeId;
    if (id) allIds.add(id.trim().toUpperCase());
  });
  archivedSnapshot.forEach(docSnap => {
    const id = docSnap.data().employeeId;
    if (id) allIds.add(id.trim().toUpperCase());
  });

  let prefixesToCheck = [prefix];
  if (prefix === "ADMIN-") {
    prefixesToCheck.push("ADM-"); // cover legacy formats if any
  }
  if (prefix === "INTERN-") {
    prefixesToCheck.push("INT-"); // cover legacy formats if any
  }

  let maxNum = 0;
  for (const id of allIds) {
    for (const p of prefixesToCheck) {
      if (id.startsWith(p)) {
        const numStr = id.slice(p.length);
        const num = parseInt(numStr, 10);
        if (!isNaN(num) && num > maxNum) {
          maxNum = num;
        }
      }
    }
  }

  const nextNum = maxNum + 1;
  return `${prefix}${String(nextNum).padStart(padLen, "0")}`;
}

  // ── signup (admin creates a new user) ──────────────────────
  async function signup({
    email,
    password,
    displayName,
    role,
    department,
    designation,
    employeeId,
    teamId,
    employmentType,
    joiningDate,
    internshipDuration,
    adminEmail,
    adminPassword,
  }) {
    const normalizedEmploymentType = normalizeEmploymentType(employmentType);
    const isIntern = isInternType(normalizedEmploymentType);
    const defaultLeave = isIntern
      ? { casual: 3, sick: 3, annual: 0 }
      : { casual: 12, sick: 12, annual: 21 };

    // Strict validation for required fields
    if (!displayName || !displayName.trim() || 
        !email || !email.trim() || 
        !password || !password.trim() || 
        !department || !department.trim()) {
      throw new Error("All required fields must be filled");
    }

    // Strict email format validation
    const emailRegex = /^[A-Za-z0-9][A-Za-z0-9._%+-]*@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!emailRegex.test(email.trim())) {
      throw new Error("Please enter a valid email address");
    }

    let tempApp = null;
    try {
      // 1. Generate unique employee ID if auto-generation is enabled (employeeId is blank)
      const finalEmployeeId = employeeId?.trim() 
        ? employeeId.trim() 
        : await generateUniqueEmployeeId(role, normalizedEmploymentType);

      // 2. Pre-verify Employee ID uniqueness in Firestore (both active and archived)
      const usersSnapshot = await getDocs(collection(db, "users"));
      const archivedSnapshot = await getDocs(collection(db, "archived_users"));

      let idExists = false;
      usersSnapshot.forEach((docSnap) => {
        if (docSnap.data().employeeId?.trim().toUpperCase() === finalEmployeeId.toUpperCase()) {
          idExists = true;
        }
      });
      archivedSnapshot.forEach((docSnap) => {
        if (docSnap.data().employeeId?.trim().toUpperCase() === finalEmployeeId.toUpperCase()) {
          idExists = true;
        }
      });

      if (idExists) {
        throw new Error("Employee ID already exists");
      }

      // 3. Initialize temporary Firebase app to authenticate admin and create user safely
      const appName = `TempAdminAuth-${Date.now()}-${Math.random().toString(36).substring(7)}`;
      tempApp = initializeApp(app.options, appName);
      const tempAuth = getAuth(tempApp);

      // Verify admin password properly before creating any account
      try {
        await signInWithEmailAndPassword(tempAuth, adminEmail, adminPassword);
      } catch (err) {
        throw new Error("Admin password is incorrect");
      }

      // Create new user in Firebase Auth via temporary app (admin session remains intact)
      let newUser;
      try {
        const credential = await createUserWithEmailAndPassword(tempAuth, email, password);
        newUser = credential.user;
      } catch (err) {
        if (err.code === "auth/email-already-in-use") {
          throw new Error("Email already exists");
        }
        throw new Error(err.message || "Failed to create user account.");
      }

      // Update new user profile in Firebase Auth via temporary app
      await updateProfile(newUser, { displayName });

      // Joining date: use admin-provided date (required); store as ISO string
      if (!joiningDate) {
        throw new Error("Date of Joining is required");
      }
      const joiningDateISO = joiningDate; // already "YYYY-MM-DD" from form

      // Internship end date: calculated from joiningDate + duration
      const duration = isIntern ? (parseInt(internshipDuration, 10) || 3) : 0;
      let internEndDateISO = null;
      if (isIntern) {
        const jd = new Date(joiningDateISO);
        const internEnd = new Date(jd);
        if (duration === 3) {
          internEnd.setDate(internEnd.getDate() + 90);
        } else {
          internEnd.setMonth(internEnd.getMonth() + duration);
        }
        internEndDateISO = internEnd.toISOString().split("T")[0];
      }

      // Create Firestore document
      const finalDesignation = designation?.trim() ? designation.trim() : "Staff";
      await setDoc(doc(db, "users", newUser.uid), {
        uid:              newUser.uid,
        email,
        displayName,
        employeeId:       finalEmployeeId,
        role,
        department:       department,
        designation:      finalDesignation,
        phone:            "",
        joiningDate:      joiningDateISO,
        managerId:        role === "employee" ? (teamId || null) : null,
        teamId:           role === "employee" ? (teamId || null) : null,
        employmentType:   normalizedEmploymentType,
        ...(isIntern ? {
          internshipStartDate: joiningDateISO,
          internshipEndDate:   internEndDateISO,
          internshipDuration:  duration,
          internshipStatus:    "active",
        } : {}),
        leaveBalance:     { ...defaultLeave },
        leaveEntitlement: { ...defaultLeave },
        isActive:         true,
        createdAt:        serverTimestamp(),
        updatedAt:        serverTimestamp(),
      });

      return newUser;
    } finally {
      if (tempApp) {
        try {
          await deleteApp(tempApp);
        } catch (e) {
          console.error("Error deleting tempApp:", e);
        }
      }
    }
  }

  // ── Derived helpers ────────────────────────────────────────
  const role       = userProfile?.role   ?? null;
  const isAdmin    = role === "admin";
  const isHR       = role === "hr";
  const isLead     = role === "lead";
  const isEmployee = role === "employee";

  const value = {
    currentUser,
    userProfile,
    role,
    isAdmin,
    isHR,
    isLead,
    isEmployee,
    loading,
    deactivatedError,
    setDeactivatedError,
    login,
    logout,
    resetPassword,
    signup,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
